/**
 * The in-flight GRAM ledger.
 *
 * The pre-signing balance guard compares a trade's native requirement with the
 * balance on screen. The balance on screen trails the chain by the push lane
 * (~1 s after the block), and a rapid series signs its externals far faster
 * than that: on the operator's 2026-10-27 series ten buys of 0.1 GRAM left a
 * 1.093 GRAM wallet within a few seconds, every guard saw the pre-series figure,
 * and the 11th and 12th clicks were signed — both executed on chain with
 * skipped_actions=1 (the swap could not be paid for) and burned gas.
 *
 * This ledger remembers what THIS tab has already signed against the wallet
 * and hands the guard what is genuinely left: the shown balance minus every
 * reservation whose wallet transaction the shown balance does not contain yet.
 *
 * A reservation ends on a FACT:
 *   - its wallet transaction's lt is known (the inclusion push) and a balance
 *     with lt ≥ it is on screen — the debit is now inside the shown number;
 *   - the external is proven never delivered (or never sent) — nothing left;
 *   - a terminal without an inclusion lt: a balance on screen whose chain
 *     time (as_of = the wallet tx's `now`) is at or past the trade — the row's
 *     second, or for the seqno backstop / fast-fail verdict the verdict's
 *     moment on the server clock (the send precedes both). A sibling's push
 *     with a newer lt but an earlier second does NOT end it.
 * A reservation nothing ever resolves expires after UNBOUND_TTL_MS, so a lost
 * push costs a late number, never a wallet frozen until reload.
 *
 * The amount reserved is the SAME figure the guard requires
 * (nativeRequiredForPreparedPlan: outer value + network fee [+ a buy's
 * deferred 1% and its external's cost]), in two tickets for a buy: the swap's
 * (ends by the rules above) and the deferred fee's (deferredFeeReserveForPlan;
 * never bound to the swap's lt: after the swap lands the wallet still holds
 * the owed fee). The fee's ticket is settled (settleNativeSpend, no chain
 * second) when its own fee external is seen applied, so the as_of rule ends
 * it on a balance at least that new.
 * It over-states the real debit by the change the wallet gets back, which is
 * the safe direction: one refused click, not one burned transaction.
 *
 * Known bound: per browser TAB, like the sell ledger. Another tab or device
 * spends the same wallet unseen; closing that needs a server-side guard.
 */

import { serverNowMs } from './serverClock'

const UNBOUND_TTL_MS = 90_000

type Reservation = {
  wallet: string
  amount: bigint
  /** lt of the wallet transaction that carries this trade's send. */
  sendLt: bigint | null
  /** The relay's name for this trade's external (the confirmation's in_msg_hash). */
  externalHash: string | null
  /** Terminal without an lt: released by a balance whose chain time
      (as_of = the wallet tx's `now`) is at least this. */
  releaseAtChainMs: number | null
  createdAtMs: number
  /** Bound for a reservation nothing resolves (UNBOUND_TTL_MS by default). */
  ttlMs: number
}

type Shown = { balanceNano: string; lt: bigint | null; asOfMs: number | null }

const reservations = new Map<number, Reservation>()
const shownByWallet = new Map<string, Shown>()
let nextTicket = 1

function parseNano(value: string | null | undefined): bigint | null {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) return null
  return BigInt(value)
}

function parseLt(value: string | bigint | null | undefined): bigint | null {
  if (typeof value === 'bigint') return value >= 0n ? value : null
  return parseNano(value ?? null)
}

function settle(wallet: string): void {
  const shown = shownByWallet.get(wallet)
  const t = Date.now()
  for (const [ticket, r] of reservations) {
    if (r.wallet !== wallet) continue
    if (t - r.createdAtMs > r.ttlMs) {
      reservations.delete(ticket)
      continue
    }
    if (!shown) continue
    if (r.sendLt !== null && shown.lt !== null && shown.lt >= r.sendLt) {
      reservations.delete(ticket)
    } else if (
      r.sendLt === null &&
      r.releaseAtChainMs !== null &&
      shown.asOfMs !== null &&
      shown.asOfMs >= r.releaseAtChainMs
    ) {
      reservations.delete(ticket)
    }
  }
}

/**
 * The balance hook reports every value it PAINTS, with the lt and chain time it
 * describes. Reported at paint, not at admission: a held value is not on
 * screen, so its debits are not inside the number the guard reads.
 */
export function noteShownNativeBalance(
  wallet: string | null,
  balanceNano: string | null,
  lt: string | bigint | null,
  asOf: string | null = null
): void {
  if (!wallet || balanceNano === null) return
  const parsedLt = parseLt(lt)
  // lt order: a late, older (or unorderable) value never replaces the record
  // the signing edge reads — another hook instance or a REST answer can lag.
  const stored = shownByWallet.get(wallet)?.lt ?? null
  if (stored !== null && (parsedLt === null || parsedLt < stored)) return
  const asOfMs = asOf ? Date.parse(asOf) : Number.NaN
  shownByWallet.set(wallet, {
    balanceNano,
    lt: parsedLt,
    asOfMs: Number.isFinite(asOfMs) ? asOfMs : null,
  })
  settle(wallet)
}

/** The balance the hook last painted for this wallet, or null. */
export function shownNativeBalanceNano(wallet: string | null): string | null {
  if (!wallet) return null
  return shownByWallet.get(wallet)?.balanceNano ?? null
}

/** Σ of the reservations the shown balance does not contain yet. */
export function reservedNativeNano(wallet: string | null): bigint {
  if (!wallet) return 0n
  settle(wallet)
  let total = 0n
  for (const r of reservations.values()) if (r.wallet === wallet) total += r.amount
  return total
}

/**
 * What the guard may compare a requirement with: the shown balance minus this
 * tab's unlanded spend. null stays null (unknown is not zero); never negative.
 */
export function availableNativeNano(
  wallet: string | null,
  shownBalanceNano: string | null
): string | null {
  const shown = parseNano(shownBalanceNano)
  if (shown === null) return null
  const reserved = reservedNativeNano(wallet)
  return (shown > reserved ? shown - reserved : 0n).toString()
}

/** Commits a signed trade's native requirement. Returns its ticket. A caller
    whose obligation provably outlives UNBOUND_TTL_MS (a buy's late-fill fee
    window) passes its own bound and ends the ticket itself. */
export function reserveNativeSpend(
  wallet: string,
  amountNano: bigint,
  ttlMs: number = UNBOUND_TTL_MS
): number {
  const ticket = nextTicket++
  reservations.set(ticket, {
    wallet,
    amount: amountNano > 0n ? amountNano : 0n,
    sendLt: null,
    externalHash: null,
    releaseAtChainMs: null,
    createdAtMs: Date.now(),
    ttlMs,
  })
  return ticket
}

/** The inclusion push named the wallet transaction: end it when that is shown. */
export function bindNativeSpendLt(
  ticket: number | null | undefined,
  lt: string | bigint | null
): void {
  if (typeof ticket !== 'number') return
  const r = reservations.get(ticket)
  const parsed = parseLt(lt)
  if (!r || parsed === null) return
  r.sendLt = parsed
  settle(r.wallet)
}

/** Wallet confirmations no ticket was named for yet (the push can beat the
    relay's answer), so a ticket named a moment later still finds its lt. */
const RECENT_CONFIRMATIONS = 16
const recentConfirmations: Array<{ wallet: string; hash: string; lt: bigint }> = []

/**
 * The relay named this ticket's external. From now on the wallet's own
 * confirmation of it ends the ticket by the lt rule, whoever hears it: the
 * attempt that took the ticket can be gone — its executor unmounts on a route
 * change (/:addr ↔ /:left/:right), and such a ticket used to subtract its
 * GRAM from every guard until the TTL (operator 2026-11-03: "Insufficient
 * GRAM" on a balance that covered the buy, until a reload).
 */
export function noteNativeSpendExternal(
  ticket: number | null | undefined,
  externalHash: string | null | undefined
): void {
  if (typeof ticket !== 'number' || !externalHash) return
  const r = reservations.get(ticket)
  if (!r) return
  r.externalHash = externalHash
  const seen = recentConfirmations.find((c) => c.wallet === r.wallet && c.hash === externalHash)
  if (seen && r.sendLt === null) {
    r.sendLt = seen.lt
    settle(r.wallet)
  }
}

/**
 * A wallet.confirmation push (status finalized) for `wallet`, from a listener
 * that lives as long as the tab (the balance hook), not as long as a page.
 */
export function bindNativeSpendByExternal(
  wallet: string | null,
  inMsgHash: string | null | undefined,
  lt: string | bigint | null | undefined
): void {
  const parsed = parseLt(lt ?? null)
  if (!wallet || !inMsgHash || parsed === null) return
  // Every balance-hook instance hears the same push: one slot per (wallet,
  // hash), or a dozen copies of one confirmation evict the others.
  if (!recentConfirmations.some((c) => c.wallet === wallet && c.hash === inMsgHash)) {
    recentConfirmations.push({ wallet, hash: inMsgHash, lt: parsed })
    if (recentConfirmations.length > RECENT_CONFIRMATIONS) recentConfirmations.shift()
  }
  let bound = false
  for (const r of reservations.values()) {
    if (r.wallet !== wallet || r.externalHash !== inMsgHash || r.sendLt !== null) continue
    r.sendLt = parsed
    bound = true
  }
  if (bound) settle(wallet)
}

/**
 * The attempt reached a terminal on which the wallet DID execute (a trade row,
 * the seqno backstop, or executed-without-trade). With a send lt nothing
 * changes — the lt rule ends it. Without one it ends on a balance whose chain
 * time is at or past the trade: the row's second when there is a row, else
 * the verdict's own moment on the server clock (the send precedes both).
 * Nothing shown that new → the TTL. Never released on the spot: the shown
 * value may still be the pre-trade one (review 2026-11-02).
 */
export function settleNativeSpend(
  ticket: number | null | undefined,
  tradeChainSeconds?: number
): void {
  if (typeof ticket !== 'number') return
  const r = reservations.get(ticket)
  if (!r || r.sendLt !== null) return
  r.releaseAtChainMs =
    typeof tradeChainSeconds === 'number' &&
    Number.isFinite(tradeChainSeconds) &&
    tradeChainSeconds > 0
      ? tradeChainSeconds * 1000
      : serverNowMs()
  settle(r.wallet)
}

/** Proven never delivered / never sent: the wallet never paid for it. */
export function releaseNativeSpend(ticket: number | null | undefined): void {
  if (typeof ticket !== 'number') return
  reservations.delete(ticket)
}

/** Test seam. */
export function resetNativeSpendReserveForTest(): void {
  reservations.clear()
  shownByWallet.clear()
  recentConfirmations.length = 0
}
